import { Dispatch, SetStateAction, useState, useContext, MouseEvent, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router-dom'
import { Button, MenuItem } from '@creditinfo-ui/atoms'
import { Context } from '../Wrapper'

interface NavbarMobileProps {
  menuOpen: boolean
  setMenuOpen: Dispatch<SetStateAction<boolean>>
  toggleMobileAccountSettings: boolean
  setToggleMobileAccountSettings: Dispatch<SetStateAction<boolean>>
  user: User | null
  users: User[]
  signIn: () => void
  signOut: () => Promise<void>
  selectLanguage: (language: string) => void
  getLink: (menuItem: string) => string | undefined
  isActive: (lang: string) => boolean
  dropdownLinks: { [key: string]: string }
  selectUser: (userId: number) => Promise<void>
}

function NavbarMobile ({
  menuOpen,
  setMenuOpen,
  user,
  users,
  signIn,
  signOut,
  selectLanguage,
  getLink,
  isActive,
  selectUser,
  dropdownLinks,
  toggleMobileAccountSettings, setToggleMobileAccountSettings
}: NavbarMobileProps): JSX.Element {
  const context = useContext(Context)
  const [toggleMobileSubmenu, setToggleMobileSubmenu] = useState(false)

  const handleToggleMobileSubmenu = useCallback((): void => {
    setToggleMobileSubmenu(prev => !prev)
  }, [setToggleMobileSubmenu])

  const handleToggleMobileAccountSettings = useCallback((): void => {
    setToggleMobileAccountSettings(prev => !prev)
  }, [setToggleMobileAccountSettings])

  const handleSignOut = useCallback(async (): Promise<void> => {
    await signOut()
  }, [signOut])

  const handleSelectUser = useCallback(async (userId: number): Promise<void> => {
    await selectUser(userId)
  }, [selectUser])

  const handleSignIn = useCallback((): void => {
    signIn()
    setMenuOpen(false)
  }, [signIn, setMenuOpen])

  const handleSelectLanguage = useCallback((lang: string, e: MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    e.preventDefault()
    selectLanguage(lang)
  }, [selectLanguage])

  const renderDropdownMenuItems = (dropdownLinks: { [key: string]: string }): JSX.Element[] => {
    return Object.entries(dropdownLinks).map(([key, href]) => (
      <a
        className="nav-link block text-white"
        href={href}
        key={key}
      >
        <FormattedMessage id={`header.${key}`} />
      </a>
    ))
  }

  return (
    <div
      className={`navbar-mobile 2xl:hidden fixed left-0 w-full bg-gray-800 text-white
        transition-transform transition-opacity duration-300 ease-out
        ${menuOpen ? 'animate-slideDown opacity-100 visible' : 'animate-slideUp opacity-0 invisible'}`}
      style={{
        transform: menuOpen ? 'translateY(0)' : 'translateY(58px)',
        transition: 'all 0.3s ease-out'
      }}
    >
      <div className='items-center navbar-nav'>
        <ul className={`${toggleMobileAccountSettings ? 'invisible hidden' : 'visible'}`}>
          <li className="nav-item main-item dropdown relative group">
            <span
              className="cursor-pointer flex items-center nav-link dropdown-nav-item"
              onClick={handleToggleMobileSubmenu}
            >
              <span className={`flex nav-link ${toggleMobileSubmenu ? 'active-dropdown' : ''}`}>
                <FormattedMessage id="header.menuItem1" />
                <svg
                  className={`ml-1 mt-1 ${toggleMobileSubmenu ? 'rotate-180' : ''}`}
                  fill="currentColor"
                  height="15"
                  viewBox="0 0 24 24"
                  width="15"
                >
                  <path
                    d="M4.29289 8.29289C4.68342 7.90237 5.31658 7.90237 5.70711 8.29289L12 14.5858L18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289C20.0976 8.68342 20.0976 9.31658 19.7071 9.70711L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L4.29289 9.70711C3.90237 9.31658 3.90237 8.68342 4.29289 8.29289Z"
                  />
                </svg>
              </span>
            </span>
            <div className={`dropdown-menu ${toggleMobileSubmenu ? '' : 'hidden'}`}>
              {renderDropdownMenuItems(dropdownLinks)}
            </div>
          </li>
          <li>
            <a
              className="nav-item main-item text-white"
              href={getLink('menuItem2')}
            >
              <FormattedMessage id="header.menuItem2" />
            </a>
          </li>
          <li>
            <a
              className="nav-item main-item text-white active"
              href={getLink('menuItem3')}
            >
              <FormattedMessage id="header.menuItem3" />
            </a>
          </li>
          <li>
            <a
              className="nav-item main-item text-white font-bold"
              href={getLink('menuItem4')}
            >
              <FormattedMessage id="header.menuItem4" />
            </a>
          </li>
        </ul>

        <div
          className={`transition-transform transition-opacity duration-300 ease-out
          ${toggleMobileAccountSettings ? 'animate-slideDown opacity-100 visible' : 'animate-slideUp hidden'}`}
        >
          <ul>
            <li className="ml-3 mb-2">
              <Button
                onClick={handleToggleMobileAccountSettings}
                size="small"
                // @ts-expect-error
                variant={['white', { isOutlined: true }]}
              >
                <svg
                  className="mr-1"
                  fill="none"
                  height="16"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="19"
                    x2="5"
                    y1="12"
                    y2="12"
                  />
                  <polyline points="12 19 5 12 12 5" />
                </svg>

                <FormattedMessage id="footer.back" />
              </Button>
            </li>
            {users && user
              ? users.map((us: any) => (
                <MenuItem
                  className={us.id === user.id ? 'font-bold' : ''}
                  key={us.id}
                  onClick={async () => await handleSelectUser(us.id)}
                >
                  {us.companyName}
                </MenuItem>
              ))
              : null}
            <li>
              <Link
                className="nav-item main-item text-white font-normal"
                to="/archive"
              >
                <FormattedMessage id="navbar.archive" />
              </Link>
            </li>
            <li>
              <hr className="main-item border-gray-light my-2" />
            </li>
          </ul>
          <div className="flex justify-center w-100">
            <Button
              className="mt-6"
              onClick={handleSignOut}
              size="small"
            >
              <FormattedMessage id="navbar.signout" />
            </Button>
          </div>
        </div>
      </div>
      <div className="navbar-mobile-footer">
        {user
          ? (
            <div
              className="flex items-center gap-2 hover:underline font-normal text-white cursor-pointer underline-offset-4"
              onClick={handleToggleMobileAccountSettings}
            >
              <div className="max-w-[140px]">
                <p className="truncate">
                  {user.companyName}
                </p>
              </div>

              <svg
                className={`transition-all ${toggleMobileAccountSettings ? 'rotate-180' : ''}`}
                fill="currentColor"
                height="15"
                viewBox="0 0 24 24"
                width="15"
              >
                <path d="M4.29289 8.29289C4.68342 7.90237 5.31658 7.90237 5.70711 8.29289L12 14.5858L18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289C20.0976 8.68342 20.0976 9.31658 19.7071 9.70711L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L4.29289 9.70711C3.90237 9.31658 3.90237 8.68342 4.29289 8.29289Z" />
              </svg>
            </div>
            )
          : (
            <span
              className="font-normal text-white cursor-pointer hover:underline underline-offset-4 mr-1"
              onClick={handleSignIn}
            >
              <FormattedMessage id="navbar.signin" />
            </span>
            )}
        <span className="border-r border-gray-brand h-[35px] mx-4" />
        <ul className="flex lang-picker 2xl:hidden">
          {context.allLanguages.map(lang => (
            <li
              className={`nav-item d-flex ${isActive(lang) ? 'active' : 'border-transparent'}`}
              key={lang}
            >
              <a
                className="nav-link flex items-center cursor-pointer uppercase"
                href="#"
                onClick={(e) => handleSelectLanguage(lang, e as any)}
              >
                <FormattedMessage id={`navbar.language.${lang}`} />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default NavbarMobile
